import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Plus,
  Download,
  Edit,
  Trash2,
  Upload,
  Clock,
  BookOpen,
  Award,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TestPrepAdmin = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importForm, setImportForm] = useState({
    sheet_id: '10TJ1GjW0gE3jIIy4-pjSlprvXmb3ZitL6EfM8DgnWCA',
    range_name: 'GRE!A1:N5000',  // Updated to 14 columns for GRE
    exam_type: 'GRE',
    test_name: '',
    duration_minutes: 60,
    passing_score: 70
  });

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/test-prep/admin/tests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTests(response.data.tests);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const handleImportQuestions = async () => {
    if (!importForm.test_name.trim()) {
      toast.error('Please enter a test name');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/test-prep/admin/create-test-from-import`,
        importForm,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Test created with ${response.data.questions_imported} questions!`);
        setShowImportModal(false);
        setImportForm({
          ...importForm,
          test_name: '',
          exam_type: 'GRE'
        });
        fetchTests();
      }
    } catch (error) {
      console.error('Error importing questions:', error);
      toast.error(error.response?.data?.detail || 'Failed to import questions');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/test-prep/admin/tests/${testId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Test deleted successfully');
        fetchTests();
      }
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test');
    }
  };

  const handleImportAndSplit = async (examType) => {
    const defaultExams = examType === 'GRE' ? 50 : 58;
    const confirmMessage = `⚠️ This will:\n\n1. DELETE all existing ${examType} tests\n2. Import fresh questions from Google Sheets\n3. Create ${defaultExams} properly structured mock exams\n\nContinue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Authentication required. Please login as admin.');
        return;
      }
      
      toast.info(`Deleting old ${examType} tests and importing new questions... This may take a moment.`);
      
      const response = await axios.post(
        `${API}/test-prep/admin/import-and-split`,
        {
          sheet_id: importForm.sheet_id,
          exam_type: examType
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const deletedCount = response.data.deleted_old_tests || 0;
        const avgQuestions = Math.round(response.data.avg_questions_per_test || 0);
        toast.success(
          `✅ Successfully replaced ${deletedCount} old test(s) with ${response.data.total_tests_created} new ${examType} mock exams!\n\n📊 Total: ${response.data.total_questions} questions\n📝 Average: ${avgQuestions} questions per exam`,
          { duration: 6000 }
        );
        fetchTests();
      }
    } catch (error) {
      console.error('Error importing and splitting questions:', error);
      
      // Better error handling
      if (error.response?.status === 403) {
        toast.error('❌ Admin access denied! You must be logged in as shifracs@gmail.com to import tests.');
      } else if (error.response?.status === 401) {
        toast.error('❌ Authentication failed. Please login again.');
      } else {
        const errorMsg = error.response?.data?.detail || 'Failed to import and split questions';
        toast.error(`❌ ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDailyQuestions = async () => {
    if (!window.confirm('Generate 30 days of daily practice questions (10 per day)?\n\nThis will replace any existing daily questions.')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Authentication required. Please login as admin.');
        return;
      }
      
      toast.info('Generating 30 days of daily questions... Please wait.');
      
      const response = await axios.post(
        `${API}/test-prep/admin/generate-daily-questions`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(
          `✅ Successfully generated ${response.data.total_days} days of daily questions!\n\n📅 From: ${response.data.start_date}\n📅 To: ${response.data.end_date}\n📝 ${response.data.questions_per_day} questions per day`,
          { duration: 8000 }
        );
      }
    } catch (error) {
      console.error('Error generating daily questions:', error);
      
      if (error.response?.status === 403) {
        toast.error('❌ Admin access denied!');
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.detail || 'Not enough questions available';
        toast.error(`❌ ${errorMsg}`);
      } else {
        toast.error('❌ Failed to generate daily questions');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              GRE & GMAT Prep Admin 🎓
            </h1>
            <p className="text-gray-600">
              Create and manage tests for your students
            </p>
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={async () => {
                try {
                  setLoading(true);
                  const token = localStorage.getItem('token');
                  const response = await axios.post(
                    `${API}/test-prep/admin/update-durations`,
                    {},
                    { headers: { 'Authorization': `Bearer ${token}` } }
                  );
                  
                  if (response.data.success) {
                    toast.success(response.data.message);
                    fetchTests();
                  }
                } catch (error) {
                  console.error('Error updating durations:', error);
                  toast.error('Failed to update test durations');
                } finally {
                  setLoading(false);
                }
              }}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <Clock className="h-4 w-4 mr-2" />
              Set All to 60min
            </Button>
            <Button 
              onClick={async () => {
                try {
                  setLoading(true);
                  const token = localStorage.getItem('token');
                  const response = await axios.post(
                    `${API}/test-prep/admin/create-structured-tests`,
                    {},
                    { headers: { 'Authorization': `Bearer ${token}` } }
                  );
                  
                  if (response.data.success) {
                    toast.success(`Created ${response.data.tests_created.length} structured tests!`);
                    fetchTests();
                  }
                } catch (error) {
                  console.error('Error creating structured tests:', error);
                  toast.error('Failed to create structured tests');
                } finally {
                  setLoading(false);
                }
              }}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <Award className="h-4 w-4 mr-2" />
              Create Official Sections
            </Button>
            <Button 
              onClick={handleGenerateDailyQuestions}
              disabled={loading}
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-50"
            >
              <Clock className="h-4 w-4 mr-2" />
              Generate Daily Questions
            </Button>
            <Button 
              onClick={() => setShowImportModal(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Import from Google Sheets
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Tests</p>
                  <p className="text-2xl font-bold text-gray-900">{tests.length}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Questions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tests.reduce((sum, t) => sum + t.total_questions, 0)}
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Award className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Tests</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tests.filter(t => t.is_active).length}
                  </p>
                </div>
                <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Duration</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tests.length > 0 
                      ? Math.round(tests.reduce((sum, t) => sum + t.duration_minutes, 0) / tests.length) 
                      : 0} min
                  </p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Import & Split - New Feature */}
        <Card className="mb-8 border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Award className="h-6 w-6 text-orange-600" />
              <span>Quick Import: Create Multiple Mock Exams</span>
              <Badge className="bg-orange-600">New</Badge>
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Import questions and automatically split them into properly structured mock exams
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* GRE Import */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">GRE Mock Exams</h3>
                    <p className="text-sm text-gray-600">Import & create 50 mock exams</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-blue-600" />
                    <span>225 minutes per exam</span>
                  </div>
                  <div className="flex items-center">
                    <Award className="h-4 w-4 mr-2 text-blue-600" />
                    <span>92% passing score</span>
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
                    <span>~30 questions per exam</span>
                  </div>
                </div>
                <Button
                  onClick={() => handleImportAndSplit('GRE')}
                  disabled={loading}
                  className="w-full bg-blue-700 hover:bg-blue-700"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Import GRE Exams
                    </>
                  )}
                </Button>
              </div>

              {/* GMAT Import */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">GMAT Mock Exams</h3>
                    <p className="text-sm text-gray-600">Import & create 58 mock exams</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-green-600" />
                    <span>210 minutes per exam</span>
                  </div>
                  <div className="flex items-center">
                    <Award className="h-4 w-4 mr-2 text-green-600" />
                    <span>82% passing score</span>
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-green-600" />
                    <span>~30 questions per exam</span>
                  </div>
                </div>
                <Button
                  onClick={() => handleImportAndSplit('GMAT')}
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Import GMAT Exams
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">How it works:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>Clears old tests:</strong> Automatically deletes any existing tests of the same type</li>
                    <li><strong>Fresh import:</strong> Questions are imported from Google Sheets (GRE and GMAT tabs)</li>
                    <li><strong>Smart distribution:</strong> Questions are randomly distributed across multiple mock exams</li>
                    <li><strong>International standards:</strong> Each exam follows proper test structure (duration, passing score)</li>
                    <li><strong>Better practice:</strong> Students can take multiple different tests instead of repeating the same one</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tests List */}
        <Card>
          <CardHeader>
            <CardTitle>All Tests</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading tests...</p>
              </div>
            ) : tests.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No Tests Yet
                </h3>
                <p className="text-gray-600 mb-6">
                  Import questions from Google Sheets to create your first test
                </p>
                <Button 
                  onClick={() => setShowImportModal(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Import from Google Sheets
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {tests.map((test) => (
                  <Card key={test.id} className="border-2">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {test.name}
                            </h3>
                            <Badge className={test.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                              {test.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline">{test.exam_type || 'General'}</Badge>
                          </div>
                          <p className="text-gray-600 text-sm mb-3">{test.description}</p>
                          <div className="flex items-center space-x-6 text-sm text-gray-600">
                            <div className="flex items-center">
                              <BookOpen className="h-4 w-4 mr-1" />
                              {test.total_questions} Questions
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {test.duration_minutes} Minutes
                            </div>
                            <div className="flex items-center">
                              <Award className="h-4 w-4 mr-1" />
                              Pass: {test.passing_score}%
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => toast.info('Edit feature coming soon!')}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteTest(test.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Download className="h-6 w-6 text-orange-600" />
                  <span>Import Questions from Google Sheets</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Test Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., GRE Verbal Practice Test 1"
                    value={importForm.test_name}
                    onChange={(e) => setImportForm({...importForm, test_name: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exam Type
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    value={importForm.exam_type}
                    onChange={(e) => {
                      const type = e.target.value;
                      const rangeMap = {
                        'GRE': 'GRE!A1:N5000',  // 14 columns: A-N
                        'GMAT': 'GMAT!A1:H5000', // 8 columns: A-H
                        'IELTS': 'IELTS!A1:Z5000',
                        'USMLE': 'USMLE!A1:Z5000',
                        'PLAB': 'PLAB!A1:Z5000',
                        'OET': 'OET!A1:Z5000',
                        'General': 'Sheet1!A1:Z5000'
                      };
                      setImportForm({
                        ...importForm, 
                        exam_type: type,
                        range_name: rangeMap[type] || 'Sheet1!A1:Z5000'
                      });
                    }}
                  >
                    <option value="GRE">GRE</option>
                    <option value="GMAT">GMAT</option>
                    <option value="IELTS">IELTS</option>
                    <option value="USMLE">USMLE</option>
                    <option value="PLAB">PLAB</option>
                    <option value="OET">OET</option>
                    <option value="General">General</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Selecting exam type auto-updates the sheet range below
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (minutes)
                    </label>
                    <Input
                      type="number"
                      value={importForm.duration_minutes}
                      onChange={(e) => setImportForm({...importForm, duration_minutes: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Passing Score (%)
                    </label>
                    <Input
                      type="number"
                      value={importForm.passing_score}
                      onChange={(e) => setImportForm({...importForm, passing_score: parseInt(e.target.value)})}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google Sheet ID
                  </label>
                  <Input
                    type="text"
                    value={importForm.sheet_id}
                    onChange={(e) => setImportForm({...importForm, sheet_id: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sheet Range
                  </label>
                  <Input
                    type="text"
                    placeholder="GRE!A1:N5000 or GMAT!A1:H5000"
                    value={importForm.range_name}
                    onChange={(e) => setImportForm({...importForm, range_name: e.target.value})}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    For GRE: GRE!A1:N5000 (14 columns) | For GMAT: GMAT!A1:H5000 (8 columns)
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Sheet Ranges for 2000+ Questions:</p>
                      <p className="mb-2"><strong>GRE Tab:</strong> Use range <code className="bg-blue-100 px-1 py-0.5 rounded">GRE!A1:N5000</code> (14 columns)</p>
                      <p><strong>GMAT Tab:</strong> Use range <code className="bg-blue-100 px-1 py-0.5 rounded">GMAT!A1:H5000</code> (8 columns)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-gray-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm text-gray-700">
                      <p className="font-medium mb-1">Expected Sheet Format:</p>
                      <p>Column A: Question #</p>
                      <p>Column B: Question Text</p>
                      <p>Column C: Type (Multiple Choice, True/False)</p>
                      <p>Column D: Difficulty</p>
                      <p>Columns E-H: Options A, B, C, D</p>
                      <p>Column I: Correct Answer</p>
                      <p>Column J: Explanation</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowImportModal(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleImportQuestions}
                    disabled={loading}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Importing...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Import & Create Test
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestPrepAdmin;
